import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

export interface FeatureFlags {
  // Full pages
  feature_page_price_history: boolean;
  feature_page_map: boolean;
  feature_page_damage_calc: boolean;
  feature_page_submit: boolean;
  feature_page_discord_bot: boolean;
  // Home-page cards
  feature_home_price_history: boolean;
  feature_home_map: boolean;
  feature_home_damage_calc: boolean;
  feature_home_discord: boolean;
  feature_home_faq: boolean;
}

const DEFAULT_FLAGS: FeatureFlags = {
  feature_page_price_history: true,
  feature_page_map: true,
  feature_page_damage_calc: true,
  feature_page_submit: true,
  feature_page_discord_bot: true,
  feature_home_price_history: true,
  feature_home_map: true,
  feature_home_damage_calc: true,
  feature_home_discord: true,
  feature_home_faq: true,
};

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  loading: boolean;
  /** Returns true if the feature is enabled OR if the current user is an admin (admins bypass all flags) */
  isEnabled: (key: keyof FeatureFlags, isAdmin?: boolean) => boolean;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  const fetchFlags = useCallback(async () => {
    try {
      const response = await axios.get<FeatureFlags>(`${API_BASE_URL}/features`);
      setFlags({ ...DEFAULT_FLAGS, ...response.data });
    } catch {
      // On failure keep defaults (all enabled) so the site stays functional
      setFlags(DEFAULT_FLAGS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const isEnabled = (key: keyof FeatureFlags, isAdmin = false): boolean => {
    if (isAdmin) return true;
    return flags[key] ?? true;
  };

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, isEnabled, refresh: fetchFlags }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
